import { useFormik, FormikProvider } from 'formik'
import { sortBy } from 'lodash'
import PropTypes from 'prop-types'

import agent from 'agent'
import Button from 'components/common/Button'
import InputTextField from 'components/common/InputTextField'
import Icon from 'components/Icon'
import Modal, {
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalSubtitle
} from 'components/Modal'
import TeamSelect from 'components/rbac-settings/TeamSelect'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import { getCompanyName } from 'utils/company'
import { pluralize, messageFromResponseError } from 'utils/strings.ts'

export default function InviteUsersModal({
  company,
  isOpen,
  setIsOpen,
  doFetch,
  ...rest
}) {
  const { hasCompanyPrivilege, hasTeamPrivilege, RBAC } = useAuth()

  // company.orgs includes every team in the company. We need to filter the
  // teams so that only teams the current user has ADD and REMOVE privileges
  // are shown as selection options
  const orgs = hasCompanyPrivilege(
    RBAC.privileges.team.ADD_REMOVE_USER_FROM_TEAM
  )
    ? company.orgs
    : company.orgs.filter((org) =>
        hasTeamPrivilege(
          org.identifier,
          RBAC.privileges.team.ADD_REMOVE_USER_FROM_TEAM
        )
      )

  const teams = sortBy(orgs, [(team) => team.name.toLowerCase()])

  return (
    <Modal css={{ width: '32em' }} isOpen={isOpen} {...rest}>
      <ModalHeader>
        <ModalCloseButton onClick={() => setIsOpen(false)} />
        <ModalTitle>Invite Users</ModalTitle>
        <ModalSubtitle>
          <Icon
            css={{ height: theme.space[3], marginRight: theme.space[1] }}
            icon="building"
          />
          <span>{getCompanyName(company)}</span>
        </ModalSubtitle>
      </ModalHeader>
      <InviteUsersForm
        doFetch={doFetch}
        setIsOpen={setIsOpen}
        setTeamCreated={() => {}}
        teams={teams}
      />
    </Modal>
  )
}

InviteUsersModal.propTypes = {
  creatingTeam: PropTypes.bool,
  company: PropTypes.object,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  doFetch: PropTypes.func
}

function generateSummary(numberOfUsersToAdd, selectedTeams) {
  return `${numberOfUsersToAdd} ${pluralize('User', numberOfUsersToAdd)} to ${
    selectedTeams.length
  } ${pluralize('Team', selectedTeams.length)}`
}

function InviteUsersForm({ teams, doFetch, setIsOpen }) {
  const { companyId } = useAuth()
  const formik = useFormik({
    initialValues: { emails: '', selectedTeams: [] },
    onSubmit: async ({ emails, selectedTeams }) => {
      const splitEmails = emails
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean)

      try {
        // invite users to the company
        await agent.post(
          `/companies/${companyId}/invites`,
          splitEmails.map((email) => ({
            email
          }))
        )

        // For each selected team, add members to that team
        await Promise.all(
          selectedTeams.map((teamId) => {
            return agent.post(
              `/users?teamId=${teamId}`,
              splitEmails.map((email) => ({ identifier: email }))
            )
          })
        )

        toast({
          type: 'success',
          text: `Invited ${generateSummary(splitEmails.length, selectedTeams)}`
        })

        doFetch()
        setIsOpen(false)
      } catch (err) {
        toast({
          type: 'error',
          text: messageFromResponseError(
            err,
            'There was an issue inviting users. Please try again later.'
          )
        })
      }
    }
  })

  const { handleSubmit, handleChange, handleBlur, values } = formik
  const { selectedTeams, emails } = values

  const numberOfUsersToAdd = emails
    .split(',')
    .filter(Boolean)
    .map((email) => email.trim()).length

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <InputTextField
          data-test-email-input
          css={{
            marginTop: theme.space[5],
            marginBottom: theme.space[3]
          }}
          name="emails"
          placeholder="Invite by email address"
          subtext="Separate multiple email addresses using commas."
          type="text"
          value={emails}
          onBlur={handleBlur}
          onChange={handleChange}
        />

        <TeamSelect
          disabled={numberOfUsersToAdd < 1}
          selectedTeams={selectedTeams}
          teams={teams}
          onTeamClick={(team, e) => {
            handleChange({
              ...e,
              target: {
                name: 'selectedTeams',
                value:
                  selectedTeams.indexOf(team.identifier) > -1
                    ? selectedTeams.filter((id) => id !== team.identifier)
                    : selectedTeams.concat(team.identifier)
              }
            })
          }}
        />

        <Button
          data-test-invite-button
          primary
          stretch
          css={{ marginTop: theme.space[3] }}
          disabled={numberOfUsersToAdd < 1 || selectedTeams.length === 0}
          type="submit"
        >
          Invite {generateSummary(numberOfUsersToAdd, selectedTeams)}
        </Button>
      </form>
    </FormikProvider>
  )
}

InviteUsersForm.propTypes = {
  doFetch: PropTypes.func,
  setIsOpen: PropTypes.func,
  teams: PropTypes.array
}
